import { FC } from "react";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { Add, Close } from "@mui/icons-material";
import { ControlledSelect, ControlledSelectOption } from "ui";

interface RegionAssignmentProps {
  regionIds: string[];
  regionOptions: ControlledSelectOption[];
  onRegionChange: (regionIds: string[]) => void;
  disabled?: boolean;
  disabledReason?: string;
  showAddButton?: boolean;
}

const RegionAssignment: FC<RegionAssignmentProps> = ({
  regionIds,
  regionOptions,
  onRegionChange,
  disabled = false,
  disabledReason,
  showAddButton = true,
}) => {
  const theme = useTheme();
  const { palette } = theme;

  const handleRegionSelectChange = (value: string, index: number) => {
    const newRegionIds = [...regionIds];
    newRegionIds[index] = value;
    onRegionChange(newRegionIds);
  };

  const handleRemoveRegion = (index: number) => {
    if (regionIds.length === 1) {
      // If only one region, deselect it (set to empty string) instead of removing the dropdown
      const newRegionIds = [""];
      onRegionChange(newRegionIds);
    } else {
      // If multiple regions, remove this specific region
      const newRegionIds = regionIds.filter((_, i) => i !== index);
      onRegionChange(newRegionIds);
    }
  };

  const handleAddRegion = () => {
    const newRegionIds = [...regionIds, ""];
    onRegionChange(newRegionIds);
  };

  return (
    <>
      <Typography>Assign Region</Typography>
      <Box>
        {regionIds.map((regionId, index) => (
          <Box key={index} sx={{ display: "flex", gap: "8px", mb: "8px" }}>
            <ControlledSelect
              placeholderSelectEnabled={false}
              placeholderSelectLabel="Select Region"
              placeholder="Select Region"
              options={regionOptions.filter(
                (option) =>
                  !regionIds.includes(option.value) ||
                  option.value === regionId,
              )}
              value={regionId}
              disabled={disabled}
              onChange={(value) => handleRegionSelectChange(value, index)}
              sx={{ flex: 1 }}
              data-testid={`region-select-${index}`}
            />
            {(regionId !== "" || index > 0) && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                disabled={disabled}
                onClick={() => handleRemoveRegion(index)}
                sx={{ minWidth: "auto", px: "8px" }}
              >
                <Close fontSize="small" />
              </Button>
            )}
          </Box>
        ))}
        {showAddButton && regionIds.some((regionId) => regionId !== "") && (
          <Button
            variant="text"
            startIcon={<Add />}
            disabled={disabled}
            onClick={handleAddRegion}
            sx={{ mt: "8px", alignSelf: "flex-start" }}
            data-testid="add-region-button"
          >
            Add Region
          </Button>
        )}
        {disabled && disabledReason && (
          <Typography
            sx={{
              color: palette.error["main"],
              mt: "10px",
            }}
          >
            {disabledReason}
          </Typography>
        )}
      </Box>
    </>
  );
};

export default RegionAssignment;
